import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import PublicLayout from "@/components/PublicLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import CityGuide from "@/pages/CityGuide";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminBlogEditor from "@/pages/admin/AdminBlogEditor";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import SchoolReviewsPage from "@/pages/school/SchoolReviews";

import Home from "@/pages/Home";
import Schools from "@/pages/Schools";
import SchoolDetail from "@/pages/SchoolDetail";
import ScholarshipExams from "@/pages/ScholarshipExams";
import MockExams from "@/pages/MockExams";
import PastQuestions from "@/pages/PastQuestions";
import HowItWorks from "@/pages/HowItWorks";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Apply from "@/pages/Apply";
import ExamRunner from "@/pages/ExamRunner";
import ExamResult from "@/pages/ExamResult";
import ConsultancyLanding from "@/pages/ConsultancyLanding";
import OkullarIcin from "@/pages/OkullarIcin";
import SchoolArticle from "@/pages/SchoolArticle";

import ParentLayout from "@/pages/parent/ParentLayout";
import ParentDashboard from "@/pages/parent/Dashboard";
import ParentChildren from "@/pages/parent/Children";
import ParentApplications from "@/pages/parent/Applications";
import ParentPerformance from "@/pages/parent/Performance";
import ParentInvitations from "@/pages/parent/Invitations";
import ParentConsultancy from "@/pages/parent/ConsultancyForm";
import ParentNotifications from "@/pages/parent/NotificationSettings";
import ParentReferrals from "@/pages/parent/Referrals";

import SchoolLayout from "@/pages/school/SchoolLayout";
import SchoolDashboard from "@/pages/school/Dashboard";
import SchoolProfilePage from "@/pages/school/SchoolProfile";
import SchoolExamsPage from "@/pages/school/SchoolExams";
import SchoolApplicationsPage from "@/pages/school/SchoolApplications";
import SchoolInvitationsPage from "@/pages/school/SchoolInvitations";
import SchoolAnnouncements from "@/pages/school/SchoolAnnouncements";
import SchoolModules from "@/pages/school/SchoolModules";
import SchoolAnalytics from "@/pages/school/SchoolAnalytics";
import SchoolMockExams from "@/pages/school/SchoolMockExams";
import SchoolGallery from "@/pages/school/SchoolGallery";
import SchoolBrochure from "@/pages/school/SchoolBrochure";
import SchoolMessaging from "@/pages/school/SchoolMessaging";
import SchoolTargeting from "@/pages/school/SchoolTargeting";
import SchoolAuditLog from "@/pages/school/SchoolAuditLog";
import SchoolCampuses from "@/pages/school/SchoolCampuses";
import SchoolMeetings from "@/pages/school/SchoolMeetings";
import ParentMeetings from "@/pages/parent/ParentMeetings";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSchools from "@/pages/admin/AdminSchools";
import AdminQuestions from "@/pages/admin/AdminQuestions";
import AdminQuestionImport from "@/pages/admin/AdminQuestionImport";
import AdminMockExams from "@/pages/admin/AdminMockExams";
import AdminPastExams from "@/pages/admin/AdminPastExams";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminSchoolSignups from "@/pages/admin/AdminSchoolSignups";
import AdminSchoolClaims from "@/pages/admin/AdminSchoolClaims";
import SchoolSignup from "@/pages/SchoolSignup";
import PasswordReset from "@/pages/PasswordReset";
import MySubscription from "@/pages/parent/MySubscription";
import AdminSchoolArticles from "@/pages/admin/AdminSchoolArticles";
import AdminErrors from "@/pages/admin/AdminErrors";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminRefunds from "@/pages/admin/AdminRefunds";
import AdminApplicationTiers from "@/pages/admin/AdminApplicationTiers";
import SchoolTeam from "@/pages/school/SchoolTeam";
import PastExams from "@/pages/PastExams";
import PaymentStatus from "@/pages/PaymentStatus";
import PaymentTestSimulator from "@/pages/PaymentTestSimulator";
import StaticPage from "@/pages/StaticPage";
import PaymentHistory from "@/components/PaymentHistory";
import SchoolPlacements from "@/pages/school/SchoolPlacements";
import AdminPlacements from "@/pages/admin/AdminPlacements";
import AdminSiteSettings from "@/pages/admin/AdminSiteSettings";

import { SettingsProvider } from "@/lib/settings";

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <SettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <AnalyticsTracker />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route index element={<Home />} />
              <Route path="/okullar" element={<Schools />} />
              <Route path="/okul/:slug" element={<SchoolDetail />} />
              <Route path="/okul/:slug/makale/:articleSlug" element={<SchoolArticle />} />
              <Route path="/okullar-icin" element={<OkullarIcin />} />
              <Route path="/bursluluk-sinavlari" element={<ScholarshipExams />} />
              <Route path="/deneme-sinavlari" element={<MockExams />} />
              <Route path="/cikmis-sorular" element={<PastQuestions />} />
              <Route path="/nasil-calisir" element={<HowItWorks />} />
              <Route path="/tercih-danismanligi" element={<ConsultancyLanding />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/bursluluk/:il" element={<CityGuide />} />
              <Route path="/bursluluk/:il/:ilce" element={<CityGuide />} />
              <Route path="/giris" element={<Login />} />
              <Route path="/kayit" element={<Register />} />
              <Route path="/okul-kayit" element={<SchoolSignup />} />
              <Route path="/sifre-belirle" element={<PasswordReset />} />
              <Route path="/odeme-sonuc" element={<PaymentStatus />} />
              <Route path="/odeme-test" element={<PaymentTestSimulator />} />

              {/* Legal & marketing static pages */}
              <Route path="/nasil-calisir" element={<StaticPage />} />
              <Route path="/hakkimizda" element={<StaticPage />} />
              <Route path="/iletisim" element={<StaticPage />} />
              <Route path="/sss" element={<StaticPage />} />
              <Route path="/paketler" element={<StaticPage />} />
              <Route path="/kvkk-aydinlatma-metni" element={<StaticPage />} />
              <Route path="/gizlilik-politikasi" element={<StaticPage />} />
              <Route path="/cerez-politikasi" element={<StaticPage />} />
              <Route path="/kullanim-sozlesmesi" element={<StaticPage />} />
              <Route path="/mesafeli-satis-sozlesmesi" element={<StaticPage />} />
              <Route path="/iade-iptal" element={<StaticPage />} />
              <Route path="/ticari-elektronik-ileti" element={<StaticPage />} />
              <Route path="/erisilebilirlik" element={<StaticPage />} />

              {/* Past exam archives (SEO) */}
              <Route path="/lgs/cikmis-sorular" element={<PastExams examType="LGS" />} />
              <Route path="/lgs/cikmis-sorular/:year" element={<PastExams examType="LGS" />} />
              <Route path="/tyt/cikmis-sorular" element={<PastExams examType="TYT" />} />
              <Route path="/tyt/cikmis-sorular/:year" element={<PastExams examType="TYT" />} />
              <Route path="/ayt/cikmis-sorular" element={<PastExams examType="AYT" />} />
              <Route path="/ayt/cikmis-sorular/:year" element={<PastExams examType="AYT" />} />
              <Route
                path="/basvuru/:examId"
                element={<ProtectedRoute roles={["parent", "admin"]}><Apply /></ProtectedRoute>}
              />
              <Route
                path="/sinav/:sessionId"
                element={<ProtectedRoute><ExamRunner /></ProtectedRoute>}
              />
              <Route
                path="/sinav-sonuc/:sessionId"
                element={<ProtectedRoute><ExamResult /></ProtectedRoute>}
              />
            </Route>

            {/* Parent panel */}
            <Route
              path="/panel"
              element={<ProtectedRoute roles={["parent", "student"]}><ParentLayout /></ProtectedRoute>}
            >
              <Route index element={<ParentDashboard />} />
              <Route path="cocuklarim" element={<ParentChildren />} />
              <Route path="basvurularim" element={<ParentApplications />} />
              <Route path="performans" element={<ParentPerformance />} />
              <Route path="davetler" element={<ParentInvitations />} />
              <Route path="tercih-danismanligi" element={<ParentConsultancy />} />
              <Route path="referans" element={<ParentReferrals />} />
              <Route path="gorusmelerim" element={<ParentMeetings />} />
              <Route path="aboneligim" element={<MySubscription />} />
              <Route path="odemelerim" element={<PaymentHistory />} />
              <Route path="bildirimler" element={<ParentNotifications />} />
            </Route>

            {/* School panel */}
            <Route
              path="/okul-paneli"
              element={<ProtectedRoute roles={["school"]}><SchoolLayout /></ProtectedRoute>}
            >
              <Route index element={<SchoolDashboard />} />
              <Route path="profil" element={<SchoolProfilePage />} />
              <Route path="sinavlar" element={<SchoolExamsPage />} />
              <Route path="basvurular" element={<SchoolApplicationsPage />} />
              <Route path="davetler" element={<SchoolInvitationsPage />} />
              <Route path="yorumlar" element={<SchoolReviewsPage />} />
              <Route path="duyurular" element={<SchoolAnnouncements />} />
              <Route path="modullerim" element={<SchoolModules />} />
              <Route path="odemelerim" element={<PaymentHistory title="Ödemelerim & Faturalar" />} />
              <Route path="analitik" element={<SchoolAnalytics />} />
              <Route path="deneme-sinavlarim" element={<SchoolMockExams />} />
              <Route path="galeri" element={<SchoolGallery />} />
              <Route path="brosur" element={<SchoolBrochure />} />
              <Route path="mesajlar" element={<SchoolMessaging />} />
              <Route path="hedefleme" element={<SchoolTargeting />} />
              <Route path="yerlestirme-verileri" element={<SchoolPlacements />} />
              <Route path="kampusler" element={<SchoolCampuses />} />
              <Route path="gorusmeler" element={<SchoolMeetings />} />
              <Route path="degisiklikler" element={<SchoolAuditLog />} />
              <Route path="ekip" element={<SchoolTeam />} />
            </Route>

            {/* Admin panel */}
            <Route
              path="/admin"
              element={<ProtectedRoute roles={["admin"]}><AdminLayout /></ProtectedRoute>}
            >
              <Route index element={<AdminDashboard />} />
              <Route path="kullanicilar" element={<AdminUsers />} />
              <Route path="okullar" element={<AdminSchools />} />
              <Route path="sorular" element={<AdminQuestions />} />
              <Route path="sorular/toplu-yukle" element={<AdminQuestionImport />} />
              <Route path="deneme-sinavlari" element={<AdminMockExams />} />
              <Route path="cikmis-sorular" element={<AdminPastExams />} />
              <Route path="basvurular" element={<AdminApplications />} />
              <Route path="abonelikler" element={<AdminSubscriptions />} />
              <Route path="entegrasyonlar" element={<AdminIntegrations />} />
              <Route path="site-ayarlari" element={<AdminSiteSettings />} />
              <Route path="okul-basvurulari" element={<AdminSchoolSignups />} />
              <Route path="okul-sahiplik" element={<AdminSchoolClaims />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="blog/yeni" element={<AdminBlogEditor />} />
              <Route path="blog/duzenle/:id" element={<AdminBlogEditor />} />
              <Route path="yorumlar" element={<AdminReviews />} />
              <Route path="analitik" element={<AdminAnalytics />} />
              <Route path="makaleler" element={<AdminSchoolArticles />} />
              <Route path="hatalar" element={<AdminErrors />} />
              <Route path="kuponlar" element={<AdminCoupons />} />
              <Route path="iadeler" element={<AdminRefunds />} />
              <Route path="basvuru-fiyatlari" element={<AdminApplicationTiers />} />
              <Route path="yerlestirme-onay" element={<AdminPlacements />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </SettingsProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
